import React, { useState } from 'react';
import { base44 } from '@/api/base44Client';
import { Bot, Loader2, Sparkles, CheckCircle, X } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';

export default function FeedbackAIAssistant({ item, onApplySuggestion }) {
  const [loading, setLoading] = useState(false);
  const [suggestion, setSuggestion] = useState(null);
  const [error, setError] = useState(null);

  const handleAnalyze = async () => {
    setLoading(true);
    setError(null);
    setSuggestion(null);

    const prompt = `You are a feedback triage assistant for a software product called COMPASS. Analyze the following user feedback and provide your assessment.

Feedback Type: ${item.type}
Title: ${item.title}
${item.type === 'bug' ? `Steps to Reproduce: ${item.steps_to_reproduce || 'Not provided'}
Expected Behavior: ${item.expected_behavior || 'Not provided'}
Actual Behavior: ${item.actual_behavior || 'Not provided'}` : `Description: ${item.description || 'Not provided'}`}
Link/Page: ${item.link || 'Not provided'}
Current Status: ${item.status}
Current Priority: ${item.priority || 'medium'}

Please analyze and respond with:
1. suggested_priority: one of "low", "medium", "high", "critical"
2. priority_reasoning: brief explanation of why this priority
3. summary: a concise 1-2 sentence summary of the core issue/request
4. suggested_tags: array of 1-3 short category tags (e.g. "ui", "performance", "data-loss", "ux", "navigation")
5. duplicate_risk: "low", "medium", or "high" - how likely this is a duplicate of common issues
6. suggested_response: a brief professional response that could be sent to the submitter acknowledging their feedback`;

    const result = await base44.integrations.Core.InvokeLLM({
      prompt,
      response_json_schema: {
        type: "object",
        properties: {
          suggested_priority: { type: "string", enum: ["low", "medium", "high", "critical"] },
          priority_reasoning: { type: "string" },
          summary: { type: "string" },
          suggested_tags: { type: "array", items: { type: "string" } },
          duplicate_risk: { type: "string", enum: ["low", "medium", "high"] },
          suggested_response: { type: "string" }
        }
      }
    });

    setSuggestion(result);
    setLoading(false);
  };

  const [applying, setApplying] = useState(false);

  const priorityColors = {
    low: 'bg-slate-100 text-slate-700',
    medium: 'bg-blue-100 text-blue-700',
    high: 'bg-amber-100 text-amber-700',
    critical: 'bg-red-100 text-red-700'
  };

  const duplicateColors = {
    low: 'text-emerald-400',
    medium: 'text-amber-400',
    high: 'text-red-400'
  };

  if (suggestion) {
    return (
      <div className="mt-3 bg-indigo-500/5 border border-indigo-500/20 rounded-lg p-3 space-y-3">
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-2 text-indigo-400 text-xs font-medium">
            <Sparkles className="w-3.5 h-3.5" />
            AI Analysis
          </div>
          <button onClick={() => setSuggestion(null)} className="text-slate-500 hover:text-slate-300">
            <X className="w-3.5 h-3.5" />
          </button>
        </div>

        <p className="text-slate-300 text-sm">{suggestion.summary}</p>

        <div className="flex flex-wrap gap-2 items-center">
          <span className="text-xs text-slate-500">Suggested Priority:</span>
          <Badge className={priorityColors[suggestion.suggested_priority]}>
            {suggestion.suggested_priority}
          </Badge>
          <span className="text-xs text-slate-500 ml-2">— {suggestion.priority_reasoning}</span>
        </div>

        {suggestion.suggested_tags?.length > 0 && (
          <div className="flex flex-wrap gap-1.5 items-center">
            <span className="text-xs text-slate-500">Tags:</span>
            {suggestion.suggested_tags.map((tag, i) => (
              <Badge key={i} className="bg-slate-700 text-slate-300 text-xs">{tag}</Badge>
            ))}
          </div>
        )}

        <div className="text-xs">
          <span className="text-slate-500">Duplicate Risk: </span>
          <span className={duplicateColors[suggestion.duplicate_risk]}>{suggestion.duplicate_risk}</span>
        </div>

        {suggestion.suggested_response && (
          <div className="bg-slate-800/50 rounded p-2">
            <span className="text-xs text-slate-500 block mb-1">Suggested Response:</span>
            <p className="text-sm text-slate-300 italic">"{suggestion.suggested_response}"</p>
          </div>
        )}

        <div className="flex gap-2">
          <Button
            size="sm"
            className="bg-indigo-600 hover:bg-indigo-700 text-white text-xs h-7"
            disabled={applying}
            onClick={async () => {
              setApplying(true);
              await onApplySuggestion({
                priority: suggestion.suggested_priority,
                tags: suggestion.suggested_tags,
                ai_summary: suggestion.summary,
                ai_duplicate_risk: suggestion.duplicate_risk,
                admin_notes: suggestion.suggested_response,
                status: 'in_progress'
              });
              setApplying(false);
              setSuggestion(null);
            }}
          >
            <CheckCircle className="w-3 h-3 mr-1" />
            {applying ? 'Applying...' : 'Apply All Suggestions'}
          </Button>
        </div>
      </div>
    );
  }

  return (
    <Button
      variant="ghost"
      size="sm"
      onClick={handleAnalyze}
      disabled={loading}
      className="text-indigo-400 hover:text-indigo-300 hover:bg-indigo-500/10 h-7 text-xs"
    >
      {loading ? (
        <><Loader2 className="w-3 h-3 mr-1 animate-spin" /> Analyzing...</>
      ) : (
        <><Bot className="w-3 h-3 mr-1" /> AI Triage</>
      )}
    </Button>
  );
}